// Reads a UK tax code (e.g. "1257L", "SK475", "BR W1") and explains it against the standard code for the tax year.
import { taxYearOfMonth } from './payslips';
// Tax year (starting April) -> standard code. Years after the last entry use the latest one.
export const STANDARD_TAX_CODES = {
    2021: { code: '1257L', allowance: 12570 },
    2022: { code: '1257L', allowance: 12570 },
    2023: { code: '1257L', allowance: 12570 },
    2024: { code: '1257L', allowance: 12570 },
    2025: { code: '1257L', allowance: 12570 },
    2026: { code: '1257L', allowance: 12570 },
};
export function standardTaxCode(taxYear) {
    const years = Object.keys(STANDARD_TAX_CODES).map(Number).sort((a, b) => a - b);
    const year = [...years].reverse().find(y => y <= taxYear) ?? years[0];
    return STANDARD_TAX_CODES[year];
}
// Prefix S/C, then K + number, number + L/M/N/T, or BR/D0-D3/0T/NT, then optional W1/M1/X emergency markers
// (some payrolls print them together, e.g. "315L W1/M1").
export function parseTaxCode(code) {
    const m = String(code || '').toUpperCase().replace(/[\s/]/g, '')
        .match(/^(S|C)?(?:(BR|D[0-3]|0T|NT)|K(\d+)|(\d+)([LMNT]))((?:W1|M1|X)+)?$/);
    if (!m)
        return null;
    return {
        region: (m[1] || ''),
        number: m[3] ? parseInt(m[3], 10) : m[4] ? parseInt(m[4], 10) : null,
        letter: m[2] || (m[3] ? 'K' : m[5]),
        emergency: !!m[6],
    };
}
const LOWER_REASONS = [
    'Benefits from work, like a company car or private medical insurance',
    'Income that isn\'t taxed at source, like rent or self-employed income',
    'Tax owed from a previous year being collected',
    'A second job using part of your allowance',
    'Income over £100,000 reducing your allowance',
];
const FLAT_RATES = { D0: 'higher', D1: 'additional' };
const SCOTTISH_FLAT_RATES = { D0: 'intermediate', D1: 'higher', D2: 'advanced', D3: 'top' };
// Likely reasons only: we never tell the member their code is wrong.
export function explainTaxCode(code, format, taxYear = currentTaxYear()) {
    const parsed = parseTaxCode(code);
    const display = String(code || '').trim();
    const question = `Why is my tax code ${display}?`;
    if (!parsed)
        return { variant: 'unreadable', headline: 'We couldn\'t read your tax code.', reasons: [], notes: [], question: 'Tell me about my tax code' };
    const standard = standardTaxCode(taxYear);
    const notes = [];
    if (parsed.region === 'S')
        notes.push('The S means Scottish income tax rates apply to your pay.');
    if (parsed.region === 'C')
        notes.push('The C means Welsh income tax rates apply to your pay.');
    if (parsed.emergency)
        notes.push('W1, M1 or X means each pay is taxed on its own, without looking at the rest of the tax year.');
    if (parsed.letter === 'T')
        notes.push('The T means HMRC needs to review some items in your tax code.');
    if (parsed.letter === 'NT') {
        return { variant: 'notax', headline: 'Your tax code means no tax is taken from this pay.', reasons: ['HMRC agreeing this income shouldn\'t be taxed through payroll, for example if you live outside the UK'], notes, question };
    }
    if (parsed.emergency || parsed.letter === 'BR' || parsed.letter === '0T') {
        const reasons = ['Starting a new job', 'Your employer not having a P45 from your last job yet'];
        if (parsed.letter === 'BR')
            reasons.push('A second job: BR is normal there, as your main job uses your tax-free allowance');
        return { variant: 'emergency', headline: 'You might be on an emergency tax code. This can mean you\'re paying too much tax for now.', reasons, notes, question };
    }
    if (parsed.letter.startsWith('D')) {
        const rate = (parsed.region === 'S' ? SCOTTISH_FLAT_RATES : FLAT_RATES)[parsed.letter] || 'a higher';
        return { variant: 'flat', headline: `All your pay from this job is taxed at the ${rate} rate.`, reasons: ['A second job, when your main job already uses your tax-free allowance and lower tax bands'], notes, question };
    }
    const allowance = (parsed.letter === 'K' ? -1 : 1) * (parsed.number || 0) * 10;
    if (parsed.letter === 'K')
        notes.push(`A K code means ${format(-allowance)} is added to your taxable pay instead of taken off.`);
    const diff = standard.allowance - allowance;
    if (diff > 0) {
        const reasons = parsed.letter === 'N' ? ['Transferring part of your allowance to your partner (Marriage Allowance)', ...LOWER_REASONS] : LOWER_REASONS;
        return { variant: 'lower', headline: `Your tax code gives you ${format(diff)} less tax-free allowance than usual.`, reasons, notes, question };
    }
    if (diff < 0) {
        const reasons = [
            ...(parsed.letter === 'M' ? ['Receiving Marriage Allowance from your partner'] : []),
            'Job expenses you can claim, like uniform cleaning or working from home',
            'Professional fees or subscriptions you pay yourself',
        ];
        return { variant: 'higher', headline: `You get ${format(-diff)} more tax-free than usual.`, reasons, notes, question };
    }
    return { variant: 'standard', headline: `Your tax code is standard. You get the usual ${format(standard.allowance)} tax-free.`, reasons: [], notes, question: 'Tell me about my tax code' };
}
// The AI writes the card from the facts the parser has worked out, so its figures always match ours.
// Real figures go in (not the Hide Figures mask); the card masks the reply.
export function taxCodePrompt(code, taxYear = currentTaxYear()) {
    const gbp = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
    const parsed = parseTaxCode(code);
    const e = explainTaxCode(code, gbp, taxYear);
    const standard = standardTaxCode(taxYear);
    const region = parsed?.region === 'S' ? 'Scotland' : parsed?.region === 'C' ? 'Wales' : 'rest of UK';
    return [
        'You are explaining a UK tax code to a member of a personal finance app, on their payslip screen.',
        `Tax code: ${code.trim()}`,
        `Tax year: ${taxYear}/${String(taxYear + 1).slice(2)} (standard code ${standard.code}, ${gbp(standard.allowance)} tax-free allowance)`,
        `Region: ${region}. Letter: ${parsed?.letter}. Emergency marker (W1/M1/X): ${parsed?.emergency ? 'yes' : 'no'}.`,
        `What we've worked out: ${[e.headline, ...e.notes].join(' ')}`,
        ...(e.reasons.length ? [`Likely reasons to consider: ${e.reasons.join('; ')}.`] : []),
        '',
        'Write the explanation the member sees. Rules:',
        '- Plain English and UK spelling. Speak to the member as "you".',
        '- Two or three short paragraphs, under 100 words in total.',
        '- Say what the code means for them, then the likely reasons.',
        '- Say "this could be because", never "this is because". Never say or suggest the code is wrong.',
        '- Only use the figures given here. Don\'t work out any other amounts.',
        '- No emojis, no markdown, no headings, no bullet points.',
        '- Don\'t greet the member or end with a question.',
    ].join('\n');
}
function currentTaxYear(today = new Date()) {
    return taxYearOfMonth(today.getFullYear(), today.getMonth());
}
